<template>
  <v-row >
    <v-col cols="10" >
    <div class="d-flex mb-3" v-if="isTaskOwner || isAssignee">
      <strong>{{ $t("task.lblUploadTaskFiles") }}:&nbsp;</strong>
    </div>
    <form ref="assignerInputUpload" v-if="isTaskOwner || isAssignee">
        <div
          v-cloak
          @drop.prevent="addTaskFile($event)"
          @dragover.prevent
          class="parentInput"
        >
          <input
            v-show="false"
            type="file"
            id="assignerDocument"
            class="input"
            @change="taskFileChanged($event.target.files)"
            :accept="allowed_file"
          />
          <label for="assignerDocument">
            <v-icon class="mr-1" color="neutralGray1"
              >mdi-attachment</v-icon
            >
            {{ $t("agenda.AttachFile") }}
          </label>
        </div>
      </form>
      <div v-if="assignerDocuments.length > 0">
        <a-data-table
          :headers="headers"
          :items="assignerDocuments"
          dense
          fixed-header
          :height="screenHeight"
          :items-per-page="-1"
          hide-default-footer
          mobile-breakpoint="0"
          :no-data-text="$t('noData')"
          :no-results-text="$t('emptySearchRecords')"
          item-key="documentId"
          sort-by="datetime"
          :sort-desc="true"
          class="mt-4"
        >
          <template v-slot:[`item.fileName`]="{ item }">
            <v-icon class="mr-2" v-if="!item.extension"> mdi-file </v-icon>
            <v-icon class="mr-2" v-else>
              {{ fileIcons[item.extension] || "mdi-file" }}
            </v-icon>
            <a
              class="title"
              v-if="
                $security.checkMeetingTypeAccess(
                  item.meetingTypeId,
                  'OpenMeetingDocument'
                )
              "
              @click="onFileDownload(item)"
              >{{ item.fileName }}</a
            >
            <span v-else>{{ item.fileName }}</span>
          </template>

          <template v-slot:[`item.created_by`]="{ item }">
            <p class="subtitle-2 pa-0 ma-0">
              {{ item.firstName }} {{ item.lastName }}
            </p>
          </template>

          <template v-slot:[`item.size`]="{ item }">
            <p class="subtitle-2 pa-0 ma-0">
              {{ formatBytes(item.size) }}
            </p>
          </template>

          <template v-slot:[`item.datetime`]="{ item }">
            <p class="subtitle-2 pa-0 ma-0">
              {{ getDate(item.datetime) }}
            </p>
          </template>
          <template v-slot:item.action="{ item }">
            <v-tooltip bottom >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  icon
                  color="primaryDark"
                  @click="documentTaskDocument(item)"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-3"
                  :disabled="((item.formData ? true : false))"
                  ><v-icon size="24"> mdi-download</v-icon></v-btn
                >
              </template>
              <span>{{ $t("agenda.tipSaveDoc") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <a-btn
                x-small
                color="primaryDark"
                width="24"
                min-width="24"
                v-on="on"
                v-bind="attrs"
                :disabled="isDeleteDisabled(item)"
                @click.stop="deleteDocument(item)"
              >
                <v-icon size="16" color="#fff"> mdi-delete</v-icon>
              </a-btn>
            </template>
            <span>{{ $t("agenda.tipDeleteDoc") }}</span>
          </v-tooltip>
          </template>
        </a-data-table>
      </div>
      <div v-else-if="isTaskOwner || isAssignee" class="mt-4">
      <span class="body-m">{{
        $t("agenda.noMeetingMinutesDocument")
      }}</span>
    </div>
    </v-col>
    <confirm-dialog ref="confirm"></confirm-dialog>
    <MessageSheet
      :showMessage="displayMessage"
      :message="message"
      :messageType="messageType"
      @close="displayMessage = false"
    ></MessageSheet>
  </v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { MAX_UPLOAD_SIZE, allowed_file } from "../../utils/constant";
import { createUUID, convertIntlDateFormat } from "../../utils/util";
import ConfirmDialog from "../ConfirmDialog.vue";
import MessageSheet from "@/components/MessageSheet.vue";
import {
  formatBytes,
  currentDateTime,
} from "../../utils/util";
export default {
  name: "TaskDocuments",
  props: {
    task: {
      type: Object,
      required: true
    }
  },
  components: {
    ConfirmDialog,
    MessageSheet
  },
  computed: {
    ...mapState({
      userList: (state) => state.people.userList,
      deletedUserList: (state) => state.meeting.deletedUserList,
    }),
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    isTaskOwner(){
      return this.task.createdBy === this.$auth.user.email || this.isAdmin
    },
    isAssignee(){
      return this.task.assignees.some(assignee => assignee.id === this.$auth.user.user_id);
    },
    isAdmin(){
      return this.$security.checkUserAccess('DeleteTask') || this.$security.checkUserAccess('UpdateTask') || this.$security.checkUserAccess('CreateTask')
    },
    isDirty(){
      return this.assignerDocs.length > 0
    },
    assignerDocuments() {
        const taskDocs = this.task.documents ?? [];

         // Filter assignerDocs to exclude records already in taskDocs based on documentId
          const filteredAssignerDocs = this.assignerDocs.filter(
            assignerDoc =>
              !taskDocs.some(taskDoc => taskDoc.documentId === assignerDoc.documentId)
          );

        let localUserList = [...this.userList];
        localUserList = localUserList.concat(this.deletedUserList);

          // Merge taskDocs with filtered assignerDocs and map with additional user information
        return [...taskDocs, ...filteredAssignerDocs].map(doc => {
          const existingPerson = localUserList.find(elem => elem.id === doc.created_by);
          return {
            ...doc,
            email: existingPerson?.email ?? "",
            firstName: existingPerson?.firstName ?? "",
            lastName: existingPerson?.lastName ?? "",
          };
        });
      },
    screenHeight(){

      const newDocsCount = this.assignerDocuments.filter(f => f.action === "add").length;

      const baseHeight = this.assignerDocuments.length * 30 + 80;
      const additionalHeight = newDocsCount * 24;
      const maxHeight = 250;

      return Math.min(baseHeight + additionalHeight, maxHeight);
    },
    headers() {
      return [
        {
          text: this.$t("agenda.lblDocument"),
          align: "left",
          value: "fileName",
          visible: true,
          width: "40%",
          class: "header header-text",
        },
        {
          text: this.$t("lblOwner"),
          align: "left",
          value: "created_by",
          visible: true,
          width: "20%",
          class: "header header-text",
        },
        {
          text: this.$t("documents.size"),
          align: "left",
          value: "size",
          visible: false,
        },
        {
          text: this.$t("documents.modified"),
          align: "left",
          value: "lastModified",
          visible: false,
        },
        {
          text: this.$t("task.lblActions"),
          value: "action",
          sortable: false,
          align: "center",
          class: "header header-text",
          width: '150px'
        }
      ];
    },
  },
  watch: {
    assignerDocs(docs) {
      this.$emit("assignerDocs", docs);
    }
  },
  data() {
    return {
      displayMessage: false,
      message: "",
      messageType: "",
      allowed_file,
      assignerDocs: [],
    };
  },
  mounted(){

  },
  methods: {
    ...mapActions({
      updateTaskAPI: "task/updateTask",
      downloadDocumentAPI: "task/downloadTaskDocument",
      deleteTaskDocument: "task/deleteTaskDocument",
      createDocuments: "task/saveTaskDocuments",
    }),
    onResize() {
      this.screenHeight = 100;
    },
    convertIntlDateFormat,
    formatBytes,
    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },
    addTaskFile(e) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.taskFileChanged([files[0]]); 
      }
    },
    async taskFileChanged(fileList) {
      const maxSize = MAX_UPLOAD_SIZE ?? 0;
      const filesWithStatus = [];
      
      // Process the first file only (for single file upload)
      const file = fileList[0];

      if (!file) {
        return; // Exit if no file is provided
      }

      if (!allowed_file.includes(file.type)) {
        filesWithStatus.push(this.$t("fileErrorMsg", { filename: file.name }));
      } else if (file.size > maxSize) {
        filesWithStatus.push(this.$t("fileSizeErrorMsg", { maxsize: formatBytes(maxSize) }));
      }

      // Display errors if validation failed
      if (filesWithStatus.length > 0) {
        this.showMessageSheet(filesWithStatus.join("\n"), "error");
        document.getElementById("assignerDocument").value = ""; // Clear the file input
        return;
      }

      // Create and append the document object
      const documentId = createUUID();
      const formData = new FormData();
      formData.append("tenantId", this.selectedTenant);
      formData.append("topicId", "");
      formData.append("documentId", documentId);
      formData.append("meetingId", "");
      formData.append("taskId", this.task.id);
      formData.append("agendaId", "");
      formData.append("directoryId", "");
      formData.append("peopleId", "");
      formData.append("file", file);
      formData.append("chunk_upload", false);
      formData.append("filename", file.name);
      formData.append("content_type", file.type);
      formData.append("document_type", "task_document");
      formData.append("lastModified", currentDateTime(file.lastModified));

      const newDocObj = {
        documentId,
        tenantid: this.selectedTenant,
        taskId: this.task.id,
        fileName: file.name,
        mimetype: file.type,
        size: file.size,
        lastModified: currentDateTime(file.lastModified),
        formData,
        fileData: file,
        action: "add",
        created_by: this.$auth.user.user_id,
      };

      this.assignerDocs.push(newDocObj);
      try {
        await this.createDocuments(formData);
        this.showMessageSheet(this.$t("task.taskDocSaveSuccess"), "success");
        this.assignerDocs = []
      } catch (error) {
        console.error(error);
      }
      // Reset the file input for new uploads
      this.$refs.assignerInputUpload.reset();
    },
    isDeleteDisabled(item) {
    return (
      (item.created_by !== this.$auth.user.user_id) && !this.isTaskOwner && !this.isAdmin
    );
  },
    async deleteDocument(item) {
      const answer = await this.ConfirmDelete();
      if (answer) {
        if (item.documentId && item.action === "add") {
          for (var i = 0; i < this.assignerDocs.length; i++) {
            var obj = this.assignerDocs[i];
            if (item.documentId === obj.documentId) {
              this.assignerDocs.splice(i, 1);
              i--;
            }
          }
        }else{
          await this.deleteTaskDocument(item.documentId);
          const params = {
            message: this.$t("task.msgTaskDocumentDeleteSuccess"),
            messageType: "success",
          };
          this.$root.$emit("AlertMessage", params);
        }
      }
    },
    async documentTaskDocument(item) {
          if (item.action === "add") {
            this.showMessageSheet(this.$t("agenda.CannotSaveDocInfo"), "info");
          } else {
            const params = {
              documentId: item.documentId,
              filename: item.fileName,
            };
            const res = await this.downloadDocumentAPI(params);
            var contentType = res.headers["content-type"];
            var blob = new Blob([res.data], { type: contentType });
            var url = window.URL.createObjectURL(blob);
            this.downloadMessage(url, item.fileName);
          }
        },
    downloadMessage(url, name) {
      var a = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display: none";
      a.href = url;
      a.download = name;
      a.click();
      window.URL.revokeObjectURL(url);
    },
async ConfirmDelete() {
  if (
    await this.$refs.confirm.open(
      this.$t("agenda.hDeleteMessage"),
      this.$t("agenda.lblDeleteMessage")
    )
  ) {
    return true;
  }
}
  },
};
</script>
